<template>
  <CContainer class="datatable-container archived-users-datatable">
    <Loader
      :is-processing="state.isLoading"
      :loader-text="$t('common.a11y_loading_archived_users_table')"
    />
    <!-- Start Modals -->
    <!-- Unarchive message -->
    <CModal
      backdrop="static"
      :visible="!!state.showUnarchiveUsersModal"
      :close-on-backdrop="false"
    >
      <template #header-wrapper>
        <div
          class="pt-4 px-4 create-popup-header d-flex align-items-center justify-content-between"
        >
          <button
            type="button"
            class="d-inline-block bg-gradient-blue text-white rounded-circle p-1 lh-normal cursor-pointer border-0 modal-close-trigger"
            aria-label="Close unarchive message"
            @click="closeUnarchiveModal"
          >
            <span class="close-icon" aria-hidden="true"></span>
          </button>
        </div>
      </template>
      <div class="px-3 pt-3 pb-1">
        <div role="status" aria-live="polite" aria-atomic="true">
          {{
            state.reqResponse.message !=
            "Invalid error from CUS, please contact with administrator"
              ? state.reqResponse.message
              : "This change may take 24 hours to execute. Check tomorrow for this request to be updated. If no change, please contact the help desk."
          }}
        </div>
        <div class="text-center mt-3">
          <button
            @click="closeUnarchiveModal"
            class="btn p-3 bg-gradient-blue text-white ms-2"
          >
            Okay
          </button>
        </div>
      </div>
      <template #footer-wrapper>
        <div></div>
      </template>
    </CModal>
    <!-- End Modals -->
    <CRow class="justify-content-center mt-2">
      <CCol md="11" class="mb-5">
        <h1 class="text-center">Archived Users</h1>
      </CCol>
    </CRow>

    <CRow class="mt-4 justify-content-center">
      <CCol md="11">
        <div
          class="table-tabs-header mb-2 bg-white d-flex align-items-center justify-content-between flex-wrap gap-2"
        >
          <div class="me-4 d-flex align-items-stretch min-w-0">
            <h2
              id="archived-users-view-heading"
              class="table-tab active mb-0 fs-6"
            >
              {{ state.tableTabs[0]?.label }}
            </h2>
          </div>
          <div
            class="actions d-flex flex-wrap align-items-center gap-2 min-w-0"
          >
            <button
              type="button"
              class="lh-normal cursor-pointer text-accent-blue-light border-0 bg-transparent p-0"
              aria-label="Download archived users as CSV"
              @click="
                handleUsers(state.pagination.activePage, false, false, true)
              "
            >
              <i class="fi flaticon-download me-1" aria-hidden="true"></i>
              <span class="d-none d-md-inline-block">Download CSV</span>
            </button>

            <div
              class="d-flex align-items-center search-field mb-0 ms-2 ms-md-4 flex-grow-1 min-w-0"
            >
              <CInputGroup class="w-auto flex-grow-1 min-w-0 mw-100">
                <div class="input-group-prepend">
                  <span class="input-group-text lh-normal"
                    ><div>
                      <i
                        class="flaticon-magnifying-glass"
                        aria-hidden="true"
                      ></i></div
                  ></span>
                </div>
                <CFormInput
                  placeholder="Search"
                  aria-label="Search archived users"
                  v-model="state.columnSearchKeys.globalQuery"
                  @input="searchByQuery($event.target.value)"
                >
                </CFormInput>
              </CInputGroup>
            </div>
          </div>
        </div>
        <div
          class="status-guides-container pt-1 pb-2 d-flex align-items-center"
        >
          <div>
            <fieldset
              v-if="!provideUsersByApiOnly"
              class="border-0 p-0 m-0 min-inline-size-0"
            >
              <legend
                id="archived-users-unarchive-legend"
                class="mb-2 float-none w-auto fs-6 fw-normal px-0"
              >
                Please select user(s) to unarchive
              </legend>
              <div class="pl-2 d-flex align-items-center flex-wrap gap-2">
                <label
                  id="archived-users-select-all-label"
                  class="visually-hidden"
                  for="archived-users-select-all"
                >
                  Select all archived users
                </label>
                <input
                  id="archived-users-select-all"
                  type="checkbox"
                  class="me-2"
                  :checked="
                    state.selectedUsers.length === archivedUsers?.length &&
                    archivedUsers.length > 0
                  "
                  @change="selectUsers($event)"
                  @keydown="toggleCheckboxOnEnter"
                  aria-labelledby="archived-users-unarchive-legend archived-users-select-all-label"
                />
                <button
                  type="button"
                  class="px-2 py-2 bg-gradient-blue reverse text-white text-uppercase fw-bold"
                  id="archived-users-custom-btn"
                  @click="unArchiveBulk"
                >
                  Unarchive users
                </button>
              </div>
            </fieldset>
            <CCheckBox
              label="Student Number"
              :model-value="state.filterParams.student_sis_id"
              @change="changeSNFilter()"
              name="Student number"
              class="custom-checkbox archived-users-student-number-checkbox"
              :inline="true"
              :custom="true"
            />
          </div>
          <per-page-selector
            class="archived-users-per-page-selector"
            :action="setPerPage"
            :pagination="state.pagination"
          />
        </div>
        <div
          class="visually-hidden"
          :key="`archived-users-loading-status-${loadingAnnouncementKey}`"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {{ loadingAnnouncement }}
        </div>

        <!-- End of modals -->
        <CAlert
          v-if="state.reqResponse.message"
          class="mt-1 w-100"
          :color="state.reqResponse.type"
          :show="!!state.reqResponse.message"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {{ state.reqResponse.message }}
        </CAlert>

        <DataTable
          v-if="archivedUsers"
          class="datatable remove-first-th-border bg-white"
          :items="archivedUsers"
          :fields="activeDataFields"
          :sorter="{ external: true }"
          column-filter
          @update:sorter-value="sortByColumn"
        >
          <template #first_name-filter>
            <input
              v-model="state.columnSearchKeys.first_name.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
              aria-label="Filter by first name"
            />
          </template>

          <template #last_name-filter>
            <input
              v-model="state.columnSearchKeys.last_name.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
              aria-label="Filter by last name"
            />
          </template>

          <template #email-filter>
            <input
              v-model="state.columnSearchKeys.email.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
              aria-label="Filter by email"
            />
          </template>

          <template #role_id-filter>
            <input
              v-model="state.columnSearchKeys.role_id.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
              aria-label="Filter by role"
            />
          </template>

          <template #created_at-filter>
            <input
              v-model="state.columnSearchKeys.created_at.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
              aria-label="Filter by added date"
            />
          </template>

          <template #archived_at-filter>
            <input
              v-model="state.columnSearchKeys.archived_at.value"
              class="form-control form-control-sm my-2"
              @input="searchByQuery($event.target.value, 'column')"
              aria-label="Filter by archived date"
            />
          </template>

          <template #multiselect="{ item }">
            <td class="mw-auto">
              <input
                v-if="!provideUsersByApiOnly"
                v-model="state.selectedUsers"
                type="checkbox"
                :value="item.id"
                @keydown="toggleCheckboxOnEnter"
                :aria-label="
                  `Select archived user ${archivedUserRowLabel(item)}`.trim()
                "
              />
            </td>
          </template>

          <template #new>
            <td>
              <span
                class="px-3 py-2 rounded bg-gradient-blue text-white font-weight-bold"
                >Added</span
              >
            </td>
          </template>

          <template #student_sis_id="{ item }">
            <td>
              {{ item.student_sis_id ? item.student_sis_id : "N/A" }}
            </td>
          </template>

          <template #first_name="{ item }">
            <td>{{ item.first_name }}</td>
          </template>

          <template #last_name="{ item }">
            <td>{{ item.last_name }}</td>
          </template>

          <template #email="{ item }">
            <td>{{ item.email }}</td>
          </template>

          <template #role_id="{ item }">
            <td>
              {{
                item.current_role_in_school
                  ? item.current_role_in_school.name
                  : "N/A"
              }}
            </td>
          </template>

          <template #created_at="{ item }">
            <td>
              <div
                v-html="
                  item.created_at
                    ? $helpers.getHTMLDateTime(
                        item.created_at,
                        'MM/DD/YYYY',
                        'h:mm A',
                        '</br>'
                      )
                    : ''
                "
              ></div>
            </td>
          </template>

          <template #archived_at="{ item }">
            <td>
              <div
                v-html="
                  item.archived_at
                    ? $helpers.getHTMLDateTime(
                        item.archived_at,
                        'MM/DD/YYYY',
                        'h:mm A',
                        '</br>'
                      )
                    : ''
                "
              />
            </td>
          </template>

          <template #action="{ item }">
            <td>
              <button
                v-if="!provideUsersByApiOnly"
                type="button"
                class="px-3 py-1 btn btn-unarchive d-flex align-items-center border-0"
                :aria-label="`Unarchive ${archivedUserRowLabel(item)}`"
                @click="unArchive(item)"
              >
                <span class="flaticon-back-arrow" aria-hidden="true"></span>
                <span class="ml-1 text-uppercase font-weight-bold"
                  >Unarchive</span
                >
              </button>
            </td>
          </template>
        </DataTable>

        <div v-else class="bg-white p-5 text-center text-dark">
          No Available Items.
        </div>

        <Pagination
          v-if="
            state.pagination.pages &&
            state.pagination.pages > 1 &&
            !state.isLazyLoadingMode
          "
          class="cs-pagination mt-4"
          :dots="false"
          :double-arrows="true"
          :active-page="state.pagination.activePage"
          :pages="state.pagination.pages"
          size="lg"
          align="center"
          @update:active-page="setActivePage"
        >
          <template #last-button>
            <i class="flaticon-right-arrow-1"></i>
          </template>
          <template #first-button>
            <i class="flaticon-left-arrow-2"></i>
          </template>
          <template #previous-button>
            <i class="fi flaticon-left-arrow-1"></i>
          </template>
          <template #next-button>
            <i class="fi flaticon-chevron"></i>
          </template>
        </Pagination>
      </CCol>
    </CRow>
  </CContainer>
</template>

<script>
import { reactive, computed, onMounted, onUnmounted, ref, watch } from "vue"
import { isAllEntriesPerPageOption } from "@/constants/tablePaginationConstants.js"
import { attachMainLayoutLazyScroll } from "@/helpers/tableLazyScroll.js"
import { useStore } from "vuex"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import PerPageSelector from "@/v3components/PerPageSelector.vue"
import download from "@/helpers/downloadCSV.js"

export default {
  name: "ArchivedUsersDataTable",
  components: {
    Loader,
    PerPageSelector
  },
  setup() {
    const store = useStore()

    const state = reactive({
      isLoading: false,
      isLazyLoadingMode: false,
      selectedUsers: [],
      showUnarchiveUsersModal: null,
      bulkUnarchiveSuccessMessage:
        "Your request to bulk unarchive has begun. If the action can be executed users will move from the Archived User page to the Main Users page. This will take some time to complete.",
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      },
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      tableTabs: [
        {
          label: "Archived Users",
          value: "archived_users"
        }
      ],
      activeTableTab: {
        value: "archived_users"
      },
      filterParams: {
        sort: "",
        search_query: "",
        student_sis_id: false
      },
      columnSearchKeys: {
        globalQuery: "",
        first_name: { value: "", column: ["first_name"], sortBy: "first_name" },
        last_name: { value: "", column: ["last_name"], sortBy: "last_name" },
        email: { value: "", column: ["email"], sortBy: "email" },
        role_id: {
          value: "",
          column: ["role_user.name"],
          sortBy: "role_user.name"
        },
        created_at: {
          value: "",
          column: ["created_at_text"],
          sortBy: "created_at"
        },
        archived_at: {
          value: "",
          column: ["role_user.pivot.archived_at_text"],
          sortBy: "role_user.pivot.archived_at"
        }
      },
      dataTableFields: [
        { key: "multiselect", label: "", sorter: false, filter: false },
        { key: "new", label: "New", filter: false, sorter: false },
        {
          key: "student_sis_id",
          filter: false,
          sorter: false,
          label: "Student number",
          _style: "min-width:150px"
        },
        { key: "first_name", _style: "min-width:120px", label: "First Name" },
        { key: "last_name", _style: "min-width:120px", label: "Last Name" },
        { key: "email", label: "Email" },
        { key: "role_id", label: "Role" },
        { key: "created_at", _style: "min-width:140px", label: "Added Date" },
        {
          key: "archived_at",
          _style: "min-width:150px",
          label: "Archived Date"
        },
        { key: "action", label: "Action", sorter: false, filter: false }
      ]
    })

    const user = computed(() => store.getters["authentication/user"])

    const users = computed(() => store.getters["users/archivedUsers"])

    const activeSchool = computed(() => store.getters["schools/activeSchool"])

    const activeDataFields = computed(() => {
      return state.dataTableFields.filter((field) =>
        !state.filterParams.student_sis_id
          ? field.key !== "student_sis_id"
          : field
      )
    })

    const archivedUsers = computed(() => {
      return users.value
    })

    const provideUsersByApiOnly = computed(() => {
      return (
        activeSchool.value && activeSchool.value.provide_users_by_api_only == 1
      )
    })

    const loadingAnnouncementKey = ref(0)
    const loadingAnnouncement = computed(() => {
      if (state.isLoading) {
        return "Loading archived users"
      }

      const total = Number(state.pagination.total) || 0
      if (!total) {
        return "No archived users available."
      }

      return `Showing ${state.pagination.from || 0} to ${
        state.pagination.to || 0
      } of ${total} archived users.`
    })

    onMounted(() => {
      initLazyLoading()
      handleUsers()
    })

    onUnmounted(() => {
      detachMainLayoutLazyScroll?.()
    })

    let detachMainLayoutLazyScroll = null
    const initLazyLoading = () => {
      detachMainLayoutLazyScroll?.()
      detachMainLayoutLazyScroll = attachMainLayoutLazyScroll(() => {
        if (
          !state.isLazyLoadingMode ||
          !users.value ||
          !users.value.length ||
          state.pagination.total <= users.value.length
        ) {
          return
        }
        state.pagination.activePage = state.pagination.activePage + 1
        handleUsers(state.pagination.activePage, false, true)
      })
    }

    const handleUsers = (page, noLoader, isLazyLoadingMode, isExport) => {
      state.pagination.activePage = page ? page : 1
      state.isLoading = !noLoader
      const params = {
        archived: true,
        page: state.pagination.activePage,
        per_page: state.pagination.per_page.value
      }
      if (state.filterParams.student_sis_id) {
        params.student_sis_id = state.filterParams.student_sis_id
      }
      if (state.filterParams.search_query) {
        params.search_query = state.filterParams.search_query
      }
      if (state.filterParams.sort_query) {
        params.sort = state.filterParams.sort_query
      }
      if (isExport) {
        exportUsers(params)
      } else {
        getUsers(params, isLazyLoadingMode)
      }
    }

    const getUsers = (params, isLazyLoadingMode) => {
      store
        .dispatch("users/getUsers", params)
        .then((response) => {
          const data = response.data
          if (isLazyLoadingMode) {
            store.commit("users/PUSH_USERS", data.data)
          } else {
            store.commit("users/SET_USERS", data.data)
          }
          const meta = response.data.meta
          if (meta) {
            state.pagination.total = meta.total
            state.pagination.from = meta.from
            state.pagination.to = meta.to
            state.pagination.pages = Math.ceil(meta.total / meta.per_page)
            state.pagination.activePage = Number(state.pagination.activePage)
            state.pagination = JSON.parse(JSON.stringify(state.pagination))
          }
          state.isLoading = false
        })
        .catch(() => {
          state.isLoading = false
        })
    }

    const searchByQuery = (value, type) => {
      clearInterval(state.searchTimeOut)
      state.searchTimeOut = setTimeout(() => {
        handleQuerySearch(value, type)
      }, 500)
    }

    const handleQuerySearch = (value, type) => {
      const query = value.toString()
      if (
        query.replace(/\s/g, "").length > 0 ||
        query.replace(/\s/g, "").length === 0
      ) {
        state.filterParams.search_query = ""
        if (type && type === "column") {
          state.columnSearchKeys.globalQuery = ""
          for (const key in state.columnSearchKeys) {
            if (Object.hasOwnProperty.call(state.columnSearchKeys, key)) {
              const columnData = state.columnSearchKeys[key]
              if (columnData.value) {
                columnData.column.forEach((col) => {
                  if (
                    col == "role_user.name" ||
                    col == "role_user.pivot.archived_at"
                  ) {
                    state.filterParams.search_query =
                      state.filterParams.search_query +
                      `role_user.pivot.school_id:${user.value.school_id}, `
                  }
                  const searchValue =
                    col === "overlap_time_count"
                      ? columnData.value
                      : '"' + columnData.value + '"'
                  state.filterParams.search_query =
                    state.filterParams.search_query + `${col}:${searchValue}, `
                })
              }
            }
          }
          state.filterParams.search_query =
            state.filterParams.search_query.slice(0, -2)
        } else {
          resetSearchQuery()
          state.columnSearchKeys.globalQuery = query
          state.filterParams.search_query = query ? `"${query}"` : query
        }
        handleUsers()
      }
    }

    const resetSearchQuery = () => {
      state.filterParams.search_query = ""
      state.columnSearchKeys = {
        globalQuery: "",
        first_name: { value: "", column: ["first_name"], sortBy: "first_name" },
        last_name: { value: "", column: ["last_name"], sortBy: "last_name" },
        email: { value: "", column: ["email"], sortBy: "email" },
        role_id: {
          value: "",
          column: ["role_user.name"],
          sortBy: "role_user.name"
        },
        created_at: {
          value: "",
          column: ["created_at_text"],
          sortBy: "created_at"
        },
        archived_at: {
          value: "",
          column: ["role_user.pivot.archived_at_text"],
          sortBy: "role_user.pivot.archived_at"
        }
      }
    }

    const selectUsers = (e) => {
      if (e.target.checked) {
        state.selectedUsers = []
        users.value.forEach((user) => {
          state.selectedUsers.push(user.id)
        })
      } else {
        state.selectedUsers = []
      }
    }

    const toggleCheckboxOnEnter = (e) => {
      if (e.key !== "Enter") return
      e.preventDefault()
      e.currentTarget?.click?.()
    }

    const unArchive = (user) => {
      state.isLoading = true
      store
        .dispatch("users/unArchive", user)
        .then(() => {
          setTimeout(() => {
            handleUsers(1, true)
            setSuccessResponse("Successfully unarchived!")
            state.selectedUsers = []
            state.isLoading = false
            state.showUnarchiveUsersModal = true
          }, 800)
        })
        .catch((err) => {
          const response = err.response.data
          setErrorResponse(response.message, response.errors)
          state.isLoading = false
          state.showUnarchiveUsersModal = true
        })
    }

    const unArchiveBulk = () => {
      state.isLoading = true
      store
        .dispatch("users/unArchiveBulk", { users: state.selectedUsers.join() })
        .then(() => {
          setTimeout(() => {
            handleUsers(1, true)
            setSuccessResponse(state.bulkUnarchiveSuccessMessage)
            state.selectedUsers = []
            state.showUnarchiveUsersModal = true
            state.isLoading = false
          }, 800)
        })
        .catch((err) => {
          const response = err.response.data
          setErrorResponse(response.message, response.errors)
          state.isLoading = false
          state.showUnarchiveUsersModal = true
        })
    }

    const exportUsers = (params) => {
      state.isLoading = true
      params.per_page = [25, 50, 100].includes(state.pagination.per_page.value)
        ? state.pagination.per_page.value
        : "all"
      store
        .dispatch("users/exportUsers", {
          ...params,
          is_archived: true,
          student_sis_id: true
        })
        .then((response) => {
          if (response.data) {
            download.CSVExport(response.data, "archived_users")
          }
          state.isLoading = false
        })
        .catch(() => {
          state.isLoading = false
        })
    }

    const setPerPage = (option) => {
      if (option) {
        state.isLazyLoadingMode = isAllEntriesPerPageOption(option)
        state.pagination.per_page = option
        handleUsers()
      }
    }

    const sortByColumn = (column) => {
      const columnName = state.columnSearchKeys[column.column]
        ? state.columnSearchKeys[column.column].sortBy
        : column.column
      state.filterParams.sort_query = `${columnName}:${
        column.asc ? "asc" : "desc"
      }`

      handleUsers()
    }

    const setActivePage = (page) => {
      state.pagination.activePage = page
      handleUsers(state.pagination.activePage)
      const topElement = document.getElementsByClassName(
        "datatable-container"
      )[0]
      topElement.scrollIntoView({ behavior: "smooth" })
    }

    const resetResponseMessages = () => {
      state.reqResponse = {
        type: "",
        message: "",
        errors: {}
      }
    }

    const setSuccessResponse = (message) => {
      state.reqResponse = {
        type: "success",
        message: message ? message : "Success!",
        errors: {}
      }
    }

    const setErrorResponse = (message, errors) => {
      state.reqResponse = {
        type: "danger",
        message: message ? message : "Something went wrong!",
        errors: errors ? errors : {}
      }
    }

    const changeSNFilter = () => {
      state.filterParams.student_sis_id = !state.filterParams.student_sis_id
      handleUsers(state.pagination.activePage)
    }

    const closeUnarchiveModal = () => {
      state.showUnarchiveUsersModal = null
      resetResponseMessages()
    }

    const archivedUserRowLabel = (item) => {
      const name = [item?.first_name, item?.last_name].filter(Boolean).join(" ")
      if (name) {
        return item?.email ? `${name}, ${item.email}` : name
      }
      return item?.email || `user ${item?.id ?? ""}`
    }

    watch(
      () => [
        state.isLoading,
        state.pagination.from,
        state.pagination.to,
        state.pagination.total,
        state.pagination.per_page?.value
      ],
      () => {
        loadingAnnouncementKey.value += 1
      }
    )

    return {
      state,
      activeDataFields,
      archivedUsers,
      provideUsersByApiOnly,
      selectUsers,
      toggleCheckboxOnEnter,
      unArchive,
      unArchiveBulk,
      setPerPage,
      sortByColumn,
      setActivePage,
      handleUsers,
      resetResponseMessages,
      searchByQuery,
      changeSNFilter,
      closeUnarchiveModal,
      archivedUserRowLabel,
      loadingAnnouncement,
      loadingAnnouncementKey
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/_variables.scss";

.search-field :deep(.input-group:focus-within) {
  outline: 2px solid $outline-color;
  outline-offset: $outline-offset;
}

.search-field :deep(.input-group .form-control:focus) {
  box-shadow: none;
}

.modal-close-trigger:focus-visible {
  outline: 2px solid $outline-color;
  outline-offset: $outline-offset;
}
</style>
